import Alpine from 'alpinejs'
import { thumb, large } from './thumb.js'
import { formatDuration } from './duration.js'

const API = '/.netlify/functions'

Alpine.data('admin', () => ({
  // Auth
  authenticated: false,
  token: localStorage.getItem('admin_token') || '',
  password: '',
  loginError: '',
  loginLoading: false,

  // Data
  loading: false,
  invites: [],
  rsvps: [],
  rsvpMap: {},

  // Filters
  currentFilter: 'all',
  searchQuery: '',

  // Modal
  modalOpen: false,
  editingId: null,
  modalDisplay: '',
  modalNames: '',
  modalPlusOne: false,
  modalError: '',
  modalSaving: false,

  // Photos viewer
  view: 'guests',
  mediaFilter: 'all',
  photoGroups: [],
  photosLoading: false,
  photosLoaded: false,
  photosError: '',
  lightboxIndex: null,
  photoPreviewLimit: 16,
  expandedGroups: {},
  // Video durations keyed by file key, captured from each tile's
  // loadedmetadata event (preload="metadata" fetches them anyway)
  durations: {},

  // Misc
  csvImporting: false,
  copiedLinkId: null,
  copiedMessageId: null,
  deletingAll: false,
  isDev: window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',

  // --- Computed ---

  get statInvites() { return this.invites.length },

  get statAttending() {
    return this.rsvps.filter((r) => r.attending === 'yes')
      .reduce((sum, r) => sum + (Number(r.guestCount) || 0), 0)
  },

  get statDeclined() {
    return this.rsvps.filter((r) => r.attending === 'no').length
  },

  get statDriving() {
    return this.rsvps.filter((r) => r.attending === 'yes' && r.driving === true).length
  },

  get statPending() {
    const responded = new Set(this.rsvps.map((r) => r.partyId))
    return this.invites.filter((inv) => !responded.has(inv.id)).length
  },

  get filtered() {
    let list
    if (this.currentFilter === 'all') {
      list = this.invites
    } else if (this.currentFilter === 'driving') {
      list = this.invites.filter((inv) => this.rsvpMap[inv.id]?.driving === true)
    } else {
      list = this.invites.filter((inv) => this.status(inv) === this.currentFilter)
    }

    if (this.searchQuery) {
      const q = this.searchQuery.toLowerCase()
      list = list.filter((inv) =>
        inv.displayName.toLowerCase().includes(q) ||
        inv.names.some((n) => n.toLowerCase().includes(q))
      )
    }
    return [...list].sort((a, b) => {
      const aDate = this.rsvpMap[a.id]?.submittedAt
      const bDate = this.rsvpMap[b.id]?.submittedAt
      if (!aDate && !bDate) return a.displayName.localeCompare(b.displayName)
      if (!aDate) return 1
      if (!bDate) return -1
      return new Date(bDate) - new Date(aDate)
    })
  },

  // --- Helpers ---

  status(inv) {
    const rsvp = this.rsvpMap[inv.id]
    if (!rsvp) return 'pending'
    return rsvp.attending === 'yes' ? 'attending' : 'declined'
  },

  badgeText(inv) {
    const s = this.status(inv)
    if (s === 'attending') return `Attending (${this.rsvpMap[inv.id]?.guestCount || 0})`
    if (s === 'declined') return 'Declined'
    return 'Pending'
  },

  badgeClass(inv) {
    const classes = {
      attending: 'bg-emerald-50 text-emerald-600',
      declined: 'bg-red-50 text-red-500',
      pending: 'bg-amber-50 text-amber-500'
    }
    return classes[this.status(inv)]
  },

  filterClass(filter) {
    const base = 'cursor-pointer px-4 py-2 rounded-lg font-medium text-sm transition-all'
    if (filter === this.currentFilter) return `${base} bg-zinc-800 text-white`
    return `${base} bg-white text-gray-500 shadow-sm hover:text-gray-700 hover:shadow`
  },

  formatNames(inv) {
    if (inv.names.length <= 1) return inv.names[0] || ''
    return inv.names.slice(0, -1).join(', ') + ' & ' + inv.names[inv.names.length - 1]
  },

  rsvpDetails(inv) {
    const rsvp = this.rsvpMap[inv.id]
    if (!rsvp) return []
    const rows = []
    if (rsvp.attending === 'yes') {
      const names = [rsvp.guestsAttending, rsvp.plusOneName].filter(Boolean)
      const joined = names.length > 1
        ? names.slice(0, -1).join(', ') + ' & ' + names[names.length - 1]
        : names[0] || '\u2014'
      rows.push({ label: 'Guests', value: joined })
    }
    if (rsvp.attending === 'yes' && rsvp.driving !== null && rsvp.driving !== undefined) {
      rows.push({ label: 'Driving', value: rsvp.driving ? 'Yes' : 'No' })
    }
    if (rsvp.email) rows.push({ label: 'Email', value: rsvp.email, isLink: true })
    if (rsvp.message) rows.push({ label: 'Message', value: rsvp.message, isMessage: true })
    return rows
  },

  rsvpDate(inv) {
    const rsvp = this.rsvpMap[inv.id]
    if (!rsvp?.submittedAt) return ''
    return new Date(rsvp.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  },

  slugify(text) {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  },

  // --- API ---

  async apiCall(path, opts = {}) {
    const res = await fetch(`${API}/${path}`, {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
        ...(opts.headers || {})
      }
    })
    if (res.status === 401) {
      this.logout()
      throw new Error('Unauthorized')
    }
    return res
  },

  // --- Auth ---

  init() {
    this.view = this.viewFromPath()
    window.addEventListener('popstate', () => {
      this.setView(this.viewFromPath(), false)
    })
    if (this.token) this.login()
  },

  viewFromPath() {
    return window.location.pathname.replace(/\/$/, '').endsWith('/media') ? 'media' : 'guests'
  },

  setView(view, push = true) {
    if (push && view === this.view) return
    this.view = view
    if (push) history.pushState(null, '', `/admin/${view}`)
    if (view === 'media' && this.authenticated && !this.photosLoaded) this.loadPhotos()
  },

  async login() {
    const pw = this.password || this.token
    if (!pw) return
    this.token = pw
    this.loginLoading = true
    this.loginError = ''
    try {
      const res = await this.apiCall('admin-invites')
      if (res.ok) {
        localStorage.setItem('admin_token', this.token)
        this.authenticated = true
        if (this.view === 'media' && !this.photosLoaded) this.loadPhotos()
        await this.loadAll()
      }
    } catch {
      this.loginError = 'Invalid password'
      this.token = ''
    } finally {
      this.loginLoading = false
    }
  },

  logout() {
    localStorage.removeItem('admin_token')
    this.token = ''
    this.authenticated = false
    this.password = ''
  },

  // --- Data ---

  async loadAll() {
    this.loading = true
    try {
      const [invRes, rsvpRes] = await Promise.all([
        this.apiCall('admin-invites'),
        this.apiCall('admin-rsvps')
      ])
      this.invites = await invRes.json()
      this.rsvps = await rsvpRes.json()
      this.rsvpMap = {}
      this.rsvps.forEach((r) => { this.rsvpMap[r.partyId] = r })
    } catch { alert('Failed to load data. Please refresh.') } finally {
      this.loading = false
    }
  },

  // --- Actions ---

  openModal(inv) {
    this.editingId = inv ? inv.id : null
    this.modalDisplay = inv ? inv.displayName : ''
    this.modalNames = inv ? inv.names.join('\n') : ''
    this.modalPlusOne = inv ? inv.plusOne : false
    this.modalError = ''
    this.modalOpen = true
    this.$nextTick(() => this.$refs.modalDisplay?.focus())
  },

  async saveModal() {
    const displayName = this.modalDisplay.trim()
    const namesRaw = this.modalNames.trim()

    if (!displayName || !namesRaw) {
      this.modalError = 'Display name and at least one guest name are required.'
      return
    }

    const names = namesRaw.split('\n').map((n) => n.trim()).filter(Boolean)
    const id = this.editingId || this.slugify(names.join('-'))

    this.modalSaving = true
    try {
      const res = await this.apiCall('admin-invites', {
        method: 'POST',
        body: JSON.stringify({ id, displayName, names, plusOne: this.modalPlusOne })
      })
      if (!res.ok) {
        const data = await res.json()
        this.modalError = data.error
        return
      }
      this.modalOpen = false
      await this.loadAll()
    } catch { alert('Failed to save. Please try again.') } finally {
      this.modalSaving = false
    }
  },

  async deleteInvite(inv) {
    if (!confirm(`Delete invite for ${inv.displayName}?`)) return
    try {
      await this.apiCall(`admin-invites?id=${encodeURIComponent(inv.id)}`, { method: 'DELETE' })
      await this.loadAll()
    } catch { alert('Failed to delete. Please try again.') }
  },

  async clearRsvp(inv) {
    if (!confirm(`Clear reply for ${inv.displayName}?`)) return
    try {
      await this.apiCall(`admin-rsvps?partyId=${encodeURIComponent(inv.id)}`, { method: 'DELETE' })
      await this.loadAll()
    } catch { alert('Failed to clear reply. Please try again.') }
  },

  // --- Photos viewer ---

  thumb,
  large,
  formatDuration,

  get filteredPhotoGroups() {
    if (this.mediaFilter === 'all') return this.photoGroups
    const wantVideo = this.mediaFilter === 'videos'
    return this.photoGroups
      .map((g) => ({ ...g, files: g.files.filter((f) => !!f.isVideo === wantVideo) }))
      .filter((g) => g.files.length > 0)
  },

  get mediaNoun() {
    if (this.mediaFilter === 'photos') return 'photo'
    if (this.mediaFilter === 'videos') return 'video'
    return 'file'
  },

  get allPhotoFiles() {
    return this.filteredPhotoGroups.flatMap((g) => g.files.map((f) => ({ ...f, label: g.label })))
  },

  get lightboxFile() {
    return this.lightboxIndex === null ? null : this.allPhotoFiles[this.lightboxIndex]
  },

  openLightbox(file) {
    const index = this.allPhotoFiles.findIndex((f) => f.key === file.key)
    if (index === -1) return
    this.lightboxIndex = index
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
  },

  closeLightbox() {
    this.lightboxIndex = null
    document.documentElement.style.overflow = ''
    document.body.style.overflow = ''
  },

  lightboxStep(delta) {
    if (this.lightboxIndex === null) return
    const count = this.allPhotoFiles.length
    this.lightboxIndex = (this.lightboxIndex + delta + count) % count
  },

  // Autoplay with sound when the browser allows it, muted otherwise
  playVideo(el) {
    el.play().catch(() => {
      el.muted = true
      el.play().catch(() => {})
    })
  },

  visibleFiles(group) {
    if (this.expandedGroups[group.folder]) return group.files
    if (group.files.length <= this.photoPreviewLimit) return group.files
    // Leave the last grid cell for the "+X more" tile
    return group.files.slice(0, this.photoPreviewLimit - 1)
  },

  hiddenFileCount(group) {
    if (group.files.length <= this.photoPreviewLimit) return 0
    return group.files.length - (this.photoPreviewLimit - 1)
  },

  toggleGroupExpanded(group) {
    this.expandedGroups[group.folder] = !this.expandedGroups[group.folder]
  },

  get photoCount() {
    return this.filteredPhotoGroups.reduce((sum, g) => sum + g.files.length, 0)
  },

  async loadPhotos() {
    this.photosLoading = true
    this.photosError = ''
    try {
      const res = await this.apiCall('admin-uploads')
      const data = await res.json()
      this.photoGroups = data.groups || []
      this.photosLoaded = true
    } catch {
      this.photosError = 'Failed to load media. Please try again.'
    } finally {
      this.photosLoading = false
    }
  },

  async toggleHidden(file) {
    const hidden = !file.hidden
    try {
      await this.apiCall('admin-uploads', {
        method: 'POST',
        body: JSON.stringify({ key: file.key, hidden })
      })
      // Update the source object in photoGroups — `file` may be a copy
      // produced by the allPhotoFiles getter (e.g. from the lightbox)
      for (const group of this.photoGroups) {
        const source = group.files.find((f) => f.key === file.key)
        if (source) { source.hidden = hidden; break }
      }
      file.hidden = hidden
    } catch { alert('Update failed. Please try again.') }
  },

  prettySize(bytes) {
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${Math.max(1, Math.round(bytes / 1024))} KB`
  },

  // --- Share links ---

  // Must match shareCode() in netlify/functions/share-upload.js
  async shareLink(inv) {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(inv.id))
    const hex = Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('')
    return `${window.location.origin}/share?p=${hex.slice(0, 6)}`
  },

  async copyShareLink(inv) {
    const link = await this.shareLink(inv)
    try {
      await navigator.clipboard.writeText(link)
      this.copiedLinkId = inv.id
      setTimeout(() => { if (this.copiedLinkId === inv.id) this.copiedLinkId = null }, 1500)
    } catch { alert(link) }
  },

  async copyShareMessage(inv) {
    const link = await this.shareLink(inv)
    const message = 'Thank you so much for celebrating with us! 💕 ' +
      'We would love to see the wedding through your eyes — ' +
      'every photo and video you caught means the world to us. ' +
      'Here is your personal link to upload your photos and browse ' +
      `everything our guests have shared: ${link}`
    try {
      await navigator.clipboard.writeText(message)
      this.copiedMessageId = inv.id
      setTimeout(() => { if (this.copiedMessageId === inv.id) this.copiedMessageId = null }, 1500)
    } catch { alert(message) }
  },

  // --- CSV ---

  async importCsv(event) {
    const file = event.target.files[0]
    if (!file) return
    event.target.value = ''

    const text = await file.text()
    const parsed = this.parseCSV(text)

    if (parsed.length === 0) { alert('No valid rows found in CSV.'); return }
    if (!confirm(`Found ${parsed.length} invites. Import them?`)) return

    this.csvImporting = true
    try {
      const res = await this.apiCall('seed-invites', {
        method: 'POST',
        body: JSON.stringify({ invites: parsed })
      })
      const data = await res.json()
      alert(data.message || 'Done')
      await this.loadAll()
    } catch { alert('Import failed. Please try again.') } finally {
      this.csvImporting = false
    }
  },

  async exportCsv() {
    const headers = ['Display Name', 'Names', 'Plus One', 'Status', 'Guests', 'Guest Count', 'Plus One Name', 'Driving', 'Email', 'Message', 'Response Date', 'Share Link']
    const rows = await Promise.all(this.invites.map(async (inv) => {
      const rsvp = this.rsvpMap[inv.id]
      return [
        inv.displayName, inv.names.join('; '), inv.plusOne ? 'Yes' : 'No',
        rsvp ? (rsvp.attending === 'yes' ? 'Attending' : 'Declined') : 'Pending',
        rsvp?.guestsAttending || '', rsvp?.guestCount || '', rsvp?.plusOneName || '',
        rsvp?.driving == null ? '' : (rsvp.driving ? 'Yes' : 'No'),
        rsvp?.email || '', rsvp?.message || '', rsvp?.submittedAt || '',
        await this.shareLink(inv)
      ]
    }))
    const csv = [headers, ...rows].map((row) => row.map((c) => `"${String(c || '').replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `guest-list-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  },

  async deleteAll() {
    if (!confirm('Delete ALL invites and replies? This cannot be undone.')) return
    this.deletingAll = true
    try {
      const res = await this.apiCall('admin-delete-all', { method: 'POST' })
      const data = await res.json()
      alert(data.message || 'Done')
      await this.loadAll()
    } catch { alert('Delete all failed. Please try again.') } finally {
      this.deletingAll = false
    }
  },

  // --- CSV Parsing ---

  parseCSV(text) {
    const lines = text.split('\n')
    const headers = this.parseCSVLine(lines[0])
    const results = []
    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i])
      const row = {}
      headers.forEach((h, idx) => { row[h.trim()] = (values[idx] || '').trim() })
      const people = row['People'] || ''
      if (!people) continue
      const displayName = row['Envelope Name'] || people
      const plusOne = (row['Plus One'] || '').toUpperCase() === 'TRUE'
      const lastName = row['Last Name'] || ''
      const names = this.parseGuestNames(people, lastName)
      results.push({ id: this.slugify(names.join('-') || people), displayName, names, plusOne })
    }
    return results
  },

  parseCSVLine(line) {
    const result = []
    let current = ''
    let inQuotes = false
    for (let i = 0; i < line.length; i++) {
      const ch = line[i]
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') { current += '"'; i++ }
        else if (ch === '"') inQuotes = false
        else current += ch
      } else {
        if (ch === '"') inQuotes = true
        else if (ch === ',') { result.push(current); current = '' }
        else current += ch
      }
    }
    result.push(current)
    return result
  },

  parseGuestNames(people, lastName) {
    if (people.toLowerCase().startsWith('the ') && people.toLowerCase().endsWith(' family')) return [people]
    if (!people.includes('&')) return [people.trim()]
    const parts = people.split('&').map((p) => p.trim())
    const first = parts[0], second = parts[1]
    const secondWords = second.split(' '), firstWords = first.split(' ')
    const sharedLast = secondWords.length > 1 ? secondWords.slice(1).join(' ') : (firstWords.length > 1 ? firstWords.slice(1).join(' ') : lastName)
    const firstName = !first.includes(' ') && sharedLast ? first + ' ' + sharedLast : first
    const secondName = !second.includes(' ') && sharedLast ? second + ' ' + sharedLast : second
    return [firstName, secondName].filter((n) => n.toLowerCase() !== 'guest' && !n.toLowerCase().startsWith('guest '))
  }
}))
